import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  MessagesSquare,
  Users,
  Plug,
  Receipt,
  Settings,
  LogOut,
  Circle,
  Menu,
  X,
  FileText,
  Webhook,
  CreditCard,
} from "lucide-react";
import { LogoIcon } from "@/components/Logo";
const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/chat", label: "Chat", icon: MessagesSquare, testid: "nav-chat", showUnread: true },
  { to: "/app/contacts", label: "Contatos", icon: Users, testid: "nav-contacts" },
  { to: "/app/recovery", label: "Recuperação de PIX", icon: Circle, testid: "nav-recovery" },
  { to: "/app/connection", label: "Conexão", icon: Plug, testid: "nav-connection" },
  { to: "/app/integrations", label: "Integrações", icon: Webhook, testid: "nav-integrations" },
  { to: "/app/transactions", label: "Transações", icon: Receipt, testid: "nav-transactions" },
  { to: "/app/logs", label: "Logs", icon: FileText, testid: "nav-logs" },
  { to: "/app/settings", label: "Minha conta", icon: Settings, testid: "nav-settings" },
  { to: "/#precos", label: "Planos", icon: CreditCard, testid: "nav-pricing" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [uRes, sRes] = await Promise.all([
        api.get("/chat/unread-count"),
        api.get("/onboarding/status"),
      ]);
      setUnread(uRes.data.unread || 0);
      setStatus(sRes.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 8000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const whatsappStatus = status?.whatsapp_status || "disconnected";
  const isChatRoute = location.pathname.startsWith("/app/chat");
  const chatFullScreen = isChatRoute && new URLSearchParams(location.search).get("chat_full") === "1";
  const wsPillColor =
    whatsappStatus === "connected"
      ? "bg-cp-primary/10 text-cp-primary-light border-cp-primary/35"
      : whatsappStatus === "connecting"
      ? "bg-cp-accent/10 text-cp-accent border-cp-accent/35"
      : "bg-cp-elevated text-slate-400 border-white/10";
  const wsDotColor =
    whatsappStatus === "connected"
      ? "bg-cp-primary-light animate-pulse"
      : whatsappStatus === "connecting"
      ? "bg-cp-accent"
      : "bg-slate-500";

  return (
    <div className="flex min-h-[100svh] h-[100dvh] min-h-0 flex-row overflow-hidden bg-cp-bg text-slate-100 font-body">
      {/* Sidebar desktop */}
      {!chatFullScreen && (
      <aside className="hidden md:flex md:w-64 shrink-0 border-r border-white/[0.06] bg-cp-bg flex-col min-h-0 overflow-y-auto">
        <Link to="/app/dashboard" className="px-5 py-5 flex items-center gap-2.5 border-b border-white/[0.06]" data-testid="logo-link">
          <LogoIcon size={36} />
          <div>
            <div className="font-heading font-bold tracking-tighter text-lg leading-none text-slate-50">ConvertPix</div>
            <div className="text-[10px] text-cp-primary-light font-medium tracking-wide mt-0.5">{status?.tenant_name || "recovery engine"}</div>
          </div>
        </Link>

        <nav className="flex-1 p-2 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) => {
                const prefix = item.matchPrefix;
                const active = isActive || (prefix && location.pathname.startsWith(prefix));
                return `flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? "bg-cp-primary/12 text-cp-primary-light font-semibold"
                    : "text-slate-400 hover:text-slate-100 hover:bg-cp-elevated"
                }`;
              }}
            >
              <span className="flex items-center gap-3">
                <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
                <span>{item.label}</span>
              </span>
              {item.showUnread && unread > 0 && (
                <span data-testid="nav-unread-badge" className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-cp-primary text-cp-bg text-[10px] font-bold flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/[0.06] space-y-2">
          <div className="px-2 py-1 text-xs">
            <div className="text-slate-500 text-[10px] tracking-wider uppercase mb-0.5">Conta</div>
            <div className="truncate text-slate-200 text-xs" data-testid="current-user-email">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      </aside>
      )}

      {/* Main */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Global topbar */}
        {!chatFullScreen && (
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-cp-bg/85 border-b border-white/[0.06] px-4 md:px-6 py-3 flex items-center gap-3">
          <button onClick={() => setMobileMenuOpen(true)} className="md:hidden text-slate-300 p-1 rounded-md hover:bg-cp-elevated" data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>

          <div className="md:hidden flex items-center gap-2">
            <LogoIcon size={28} />
            <span className="font-heading font-bold tracking-tighter text-slate-50">ConvertPix</span>
          </div>

          <div className="flex-1" />

          {/* WhatsApp status pill */}
          <Link
            to="/app/connection"
            data-testid="topbar-whatsapp-pill"
            className={`flex items-center gap-1.5 border text-xs px-2.5 py-1.5 rounded-md font-medium transition hover:opacity-80 ${wsPillColor}`}
            title="Status do WhatsApp"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${wsDotColor}`} />
            <span className="hidden sm:inline">
              {whatsappStatus === "connected" ? "WhatsApp ativo" : whatsappStatus === "connecting" ? "Conectando" : "Conectar WhatsApp"}
            </span>
          </Link>
        </header>
        )}

        <div
          className={
            isChatRoute
              ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0"
              : "flex-1 min-h-0 min-w-0 overflow-y-auto px-4 py-6 md:px-8 md:py-8"
          }
        >
          <Outlet context={{ status, reloadStatus: loadStatus }} />
        </div>
      </main>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && !chatFullScreen && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-menu-drawer">
          <div className="flex-1 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <div className="w-72 bg-cp-bg border-l border-white/[0.06] flex flex-col shadow-2xl shadow-black/40">
            <div className="px-5 py-4 flex items-center justify-between border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <LogoIcon size={32} />
                <span className="font-heading font-bold text-slate-50">ConvertPix</span>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-slate-400 rounded-md p-1 hover:bg-cp-elevated hover:text-slate-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 p-2 space-y-0.5">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => {
                    const prefix = item.matchPrefix;
                    const active = isActive || (prefix && location.pathname.startsWith(prefix));
                    return `flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm transition-all ${
                      active ? "bg-cp-primary/12 text-cp-primary-light font-semibold" : "text-slate-300 hover:bg-cp-elevated hover:text-slate-100"
                    }`;
                  }}
                >
                  <span className="flex items-center gap-3">
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </span>
                  {item.showUnread && unread > 0 && (
                    <span className="min-w-[20px] h-[20px] px-1.5 rounded-full bg-cp-primary text-cp-bg text-[10px] font-bold flex items-center justify-center">{unread}</span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-white/[0.06]">
              <div className="px-2 py-1 text-xs text-slate-400 truncate mb-2">{user?.email}</div>
              <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10">
                <LogOut className="h-4 w-4" /> Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
